import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

/**
 * Centralized service for displaying toast notifications using ngx-toastr.
 * This service provides consistent styling and behavior for all toasts in the application.
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  constructor(private readonly toastr: ToastrService) {}

  /**
   * Display a success toast notification
   * @param message - The message to display
   * @param title - Optional title (defaults to 'Success')
   */
  success(message: string, title?: string, timeOut?: number) {
    this.toastr.success(message, title ?? 'Success', {
      timeOut: timeOut ?? 3000,
      closeButton: true,
    });
  }

  /**
   * Display an error toast notification
   * @param message - The error message to display
   * @param title - Optional title (defaults to 'Error')
   */
  error(message: string, title?: string, timeOut?: number) {
    this.toastr.error(message, title ?? 'Error', {
      timeOut: timeOut ?? 5000,
      closeButton: true,
      progressBar: true,
    });
  }

  /**
   * Display an informational toast notification
   * @param message - The message to display
   * @param title - Optional title (defaults to 'Information')
   */
  info(message: string, title?: string, timeOut?: number) {
    this.toastr.info(message, title ?? 'Information', {
      timeOut: timeOut ?? 4000,
      closeButton: true,
    });
  }

  /**
   * Display a warning toast notification
   * @param message - The warning message to display
   * @param title - Optional title (defaults to 'Warning')
   */
  warning(message: string, title?: string, timeOut?: number) {
    this.toastr.warning(message, title ?? 'Warning', {
      timeOut: timeOut ?? 4000,
      closeButton: true,
    });
  }

  /**
   * Display a brief notification toast (e.g., "Copied to clipboard")
   * @param message - The notification message
   */
  notification(message: string, timeOut?: number) {
    this.toastr.info(message, '', {
      timeOut: timeOut ?? 2000,
      closeButton: false,
    });
  }
}
